import React, { createContext, useContext, useEffect, useState } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useQuery } from "@tanstack/react-query";
import useAirlineAdminApi from "./api/useAirlineAdminApi";
import { useFormData } from "../helpers/FormDataProvider";
import { getServiceUrls } from "./serviceUrls";
import Loader from "../components/element/Loader";

const ConfigurationContext = createContext();

const ConfigurationProvider = ({ children }) => {
  const { keycloak } = useKeycloak();
  const { userData } = useFormData();
  const { fetchConfiguration } = useAirlineAdminApi(userData.isAAUser);
  const SERVICE_URL = getServiceUrls();

  const [configurationData, setConfigurationData] = useState(null);
  const [error, setError] = useState(null);
 
  const {
    data: configResponse,
    isLoading: isConfigLoading,
    error: configError,
    refetch: refetchConfig,
  } = useQuery({
   
    queryFn: async () => {
      return await fetchConfiguration();
    },
    enabled: keycloak.authenticated,
    retry: false,
  });

  // Process configuration data when it's fetched
  useEffect(() => {
    if (configResponse?.data) {
      const configMap = {};
      configResponse.data.forEach((config) => {
        configMap[config.propertyKey] = {
          value: config.propertyValue,
          description: config.propertyDescription,
          id: config.id,
          profile: config.profile,
        };
      });
      setConfigurationData(configMap);
      setError(null);
    }
  }, [configResponse]);

  // Handle errors
  useEffect(() => {
    if (configError) {
      setError(configError);
      console.error("Configuration fetch error:", configError);
    }
  }, [configError]);

  // Show loader while configuration is being fetched
  if (keycloak.authenticated && isConfigLoading && !configurationData) {
    return <Loader />;
  }

  // Helper function to get configuration value
  const getConfigValue = (propertyKey, defaultValue = null) => {
    if (!configurationData) return defaultValue;
    const config = configurationData[propertyKey];
    return config ? config.value : defaultValue;
  };

  // Helper function to get configuration object
  const getConfig = (propertyKey) => {
    if (!configurationData) return null;
    return configurationData[propertyKey] || null;
  };

  // Helper function to refresh configuration
  const refreshConfiguration = () => {
    refetchConfig();
  };

  const contextValue = {
    configurationData,
    getConfigValue,
    getConfig,
    refreshConfiguration,
    isLoading: isConfigLoading,
    error,
  };

  return (
    <ConfigurationContext.Provider value={contextValue}>
      {children}
    </ConfigurationContext.Provider>
  );
};

// Custom hook to use configuration context
export const useConfiguration = () => {
  const context = useContext(ConfigurationContext);
  if (!context) {
    throw new Error(
      "useConfiguration must be used within a ConfigurationProvider"
    );
  }
  return context;
};

export { ConfigurationContext, ConfigurationProvider };
